import React from 'react';
import { DollarSign, Wallet, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SectionWrapper, SectionHeading } from '../../components/common';
import { getVisualCmsIcon } from '@/lib/visual-cms/icon-registry';
import { CardDescriptionHtml } from '@/components/visual-cms/CardDescriptionHtml';

interface ReasonItem {
  icon: LucideIcon;
  title: string;
  desc: string;
}

export interface WhyReasonInput {
  /** Lucide registry key (e.g. "DollarSign"). */
  icon?: string | null;
  title: string;
  desc: string;
}

export interface WhySectionProps {
  title?: string;
  reasons?: WhyReasonInput[];
}

const defaultReasons: ReasonItem[] = [
  {
    icon: DollarSign,
    title: 'Save money',
    desc: 'Skip the cost of ownership for items you only use occasionally. Rent them instead.',
  },
  {
    icon: Wallet,
    title: 'Earn extra income',
    desc: 'Turn items you rarely use into extra income.',
  },
  {
    icon: Users,
    title: 'Built on Trust',
    desc: 'Verified users, secure payments, and transparent reviews every step of the way.',
  },
];

export const WhySection: React.FC<WhySectionProps> = ({ title, reasons: cmsReasons }) => {
  const heading = title?.trim() || 'Why Rent It Forward?';
  const reasons: ReasonItem[] =
    cmsReasons && cmsReasons.length > 0
      ? cmsReasons.map((r, i) => ({
          icon: getVisualCmsIcon(r.icon ?? null) ?? defaultReasons[i % defaultReasons.length].icon,
          title: r.title || defaultReasons[i % defaultReasons.length].title,
          desc: r.desc || defaultReasons[i % defaultReasons.length].desc,
        }))
      : defaultReasons;

  return (
    <SectionWrapper bg="white" py="lg" className="pt-0">
      <SectionHeading title={heading} />
      <div className="grid sm:grid-cols-3 gap-5">
        {reasons.map(({ icon: ReasonIcon, title, desc }) => (
          <div
            key={title}
            className="bg-v2-neutral-50 rounded-v2-lg p-6 text-center transition-shadow duration-200 hover:shadow-sm"
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
              <ReasonIcon size={48} strokeWidth={2} className="text-v2-green-500" />
            </div>
            <h3 className="font-display text-[18px] lg:text-[22px] font-bold text-v2-neutral-900 mb-2">{title}</h3>
            <CardDescriptionHtml
              as="p"
              text={desc}
              className="text-[16px] leading-[1.6] text-v2-neutral-600 leading-relaxed"
            />
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
};
